import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private primaryColor = new BehaviorSubject<string>('#0080F5');
  private secondaryColor = new BehaviorSubject<string>('#6C757D');

  public primaryColor$ = this.primaryColor.asObservable();
  public secondaryColor$ = this.secondaryColor.asObservable();

  constructor() {
    // Initialize with default colors
    this.applyTheme('#0080F5', '#6C757D');
  }

  /**
   * Apply theme colors to the application
   * @param primary Primary color (color_code from API)
   * @param secondary Secondary color (secondary_color from API)
   */
  applyTheme(primary: string, secondary: string): void {
    if (!primary || !secondary) {
      console.warn('ThemeService: Invalid colors provided, using defaults');
      primary = primary || '#0080F5';
      secondary = secondary || '#6C757D';
    }

    // Update BehaviorSubjects
    this.primaryColor.next(primary);
    this.secondaryColor.next(secondary);

    // Apply CSS custom properties to document root
    const root = document.documentElement;
    
    // Primary color variations
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--primary-color-rgb', this.hexToRgb(primary));
    root.style.setProperty('--primary-color-light', this.lightenColor(primary, 20));
    root.style.setProperty('--primary-color-dark', this.darkenColor(primary, 20));
    root.style.setProperty('--primary-color-hover', this.darkenColor(primary, 10));
    
    // Secondary color variations
    root.style.setProperty('--secondary-color', secondary);
    root.style.setProperty('--secondary-color-rgb', this.hexToRgb(secondary));
    root.style.setProperty('--secondary-color-light', this.lightenColor(secondary, 20));
    root.style.setProperty('--secondary-color-dark', this.darkenColor(secondary, 20));
    root.style.setProperty('--secondary-color-hover', this.darkenColor(secondary, 10));

    // Apply to Material Design components
    this.applyMaterialTheme(primary, secondary);
    
    console.log(`ThemeService: Applied theme - Primary: ${primary}, Secondary: ${secondary}`);
  }

  /**
   * Apply colors to Material Design components
   */
  private applyMaterialTheme(primary: string, secondary: string): void {
    const root = document.documentElement;
    
    // Material button colors
    root.style.setProperty('--mat-primary-color', primary);
    root.style.setProperty('--mat-accent-color', secondary);
    
    // Update existing Material components
    this.updateMaterialComponents(primary, secondary);
  }

  /**
   * Update existing Material components with new colors
   */
  private updateMaterialComponents(primary: string, secondary: string): void {
    // Update primary buttons
    const primaryButtons = document.querySelectorAll('.mat-flat-button.mat-primary, .mat-raised-button.mat-primary, .mat-fab.mat-primary, .mat-mini-fab.mat-primary');
    primaryButtons.forEach((button: any) => {
      button.style.backgroundColor = primary + ' !important';
    });

    // Update form field focus colors
    const formFields = document.querySelectorAll('.mat-form-field.mat-focused .mat-form-field-label');
    formFields.forEach((field: any) => {
      field.style.color = primary;
    });

    // Update checkboxes and radio buttons
    const checkboxes = document.querySelectorAll('.mat-checkbox-checked .mat-checkbox-background, .mat-radio-checked .mat-radio-outer-circle');
    checkboxes.forEach((checkbox: any) => {
      checkbox.style.backgroundColor = primary;
    });
  }

  /**
   * Get current primary color
   */
  getCurrentPrimaryColor(): string {
    return this.primaryColor.value;
  }

  /**
   * Get current secondary color
   */
  getCurrentSecondaryColor(): string {
    return this.secondaryColor.value;
  }

  /**
   * Convert hex color to RGB string
   */
  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `${r}, ${g}, ${b}`;
    }
    return '0, 128, 245'; // Default blue RGB
  }

  /**
   * Lighten a color by a percentage
   */
  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  /**
   * Darken a color by a percentage
   */
  private darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
      (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
      (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
  }

  /**
   * Reset theme to default colors
   */
  resetToDefault(): void {
    this.applyTheme('#0080F5', '#6C757D');
  }

  /**
   * Check if a color is valid hex color
   */
  private isValidHexColor(color: string): boolean {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  }

  /**
   * Validate and sanitize color input
   */
  private sanitizeColor(color: string): string {
    if (!color) return '';
    
    // Add # if missing
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    
    // Validate hex color
    if (this.isValidHexColor(color)) {
      return color.toUpperCase();
    }
    
    return '';
  }

  /**
   * Apply theme from company details object
   */
  applyThemeFromCompanyDetails(companyDetails: any): void {
    const primaryColor = this.sanitizeColor(companyDetails?.color_code) || '#0080F5';
    const secondaryColor = this.sanitizeColor(companyDetails?.secondary_color) || '#6C757D';
    
    this.applyTheme(primaryColor, secondaryColor);
  }
}